import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTheme } from "next-themes";
import { useMutation } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { NumerolojiAdminSidebar } from "@/components/admin/NumerolojiAdminSidebar";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";
import { cn } from "@/lib/utils";

import {
  Bell,
  CircleDollarSign,
  FileText,
  LayoutDashboard,
  ListOrdered,
  Mail,
  Moon,
  Package,
  PencilRuler,
} from "lucide-react";

type PackageKey = "pin" | "isim" | "normal" | "premium" | "iliski";

type CreateManualResponse = { ok: true; requestId: string };

function normalizePackage(input: string | null): PackageKey {
  const v = (input ?? "").toLowerCase().trim();
  if (v === "pin" || v === "isim" || v === "normal" || v === "premium" || v === "iliski") {
    return v;
  }
  return "normal";
}

function parseLang(value: string) {
  const v = value.toLowerCase().trim();
  return v === "en" || v === "es" || v === "de" ? v : "tr";
}

async function createManualRequest(payload: Record<string, unknown>) {
  const res = await adminFetch("/api/admin/numeroloji/requests/manual", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Kayıt oluşturulamadı.");
  }
  return json as CreateManualResponse;
}

export default function AdminNumerolojiManual() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { sessionQuery, loginMutation, logoutMutation } = useAdminSession();

  const { theme, setTheme, systemTheme } = useTheme();
  const resolvedTheme = theme === "system" ? systemTheme : theme;
  const isDark = resolvedTheme === "dark";
  const configured = Boolean(sessionQuery.data?.configured);
  const authenticated = Boolean(sessionQuery.data?.authenticated);

  const initialPackage = useMemo(() => normalizePackage(searchParams.get("package")), [searchParams]);

  const [selectedPackage, setSelectedPackage] = useState<PackageKey>(initialPackage);
  const [reportLang, setReportLang] = useState("tr");

  const [ad, setAd] = useState("");
  const [soyad, setSoyad] = useState("");
  const [email, setEmail] = useState("");
  const [gun, setGun] = useState("");
  const [ay, setAy] = useState("");
  const [yil, setYil] = useState("");

  const [partnerAd, setPartnerAd] = useState("");
  const [partnerSoyad, setPartnerSoyad] = useState("");
  const [partnerGun, setPartnerGun] = useState("");
  const [partnerAy, setPartnerAy] = useState("");
  const [partnerYil, setPartnerYil] = useState("");

  const isRelationship = selectedPackage === "iliski";
  const showBirthDate = selectedPackage !== "isim";

  const mutation = useMutation({
    mutationFn: createManualRequest,
    onSuccess: (data) => {
      navigate(`/admin/numeroloji/${encodeURIComponent(data.requestId)}`, { replace: true });
    },
  });

  if (sessionQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">Yükleniyor...</div>;
  }

  if (!authenticated) {
    return (
      <AdminLoginCard
        configured={configured}
        onLogin={(args) => loginMutation.mutate(args)}
        isLoading={loginMutation.isPending}
        error={loginMutation.isError ? (loginMutation.error as Error).message : undefined}
      />
    );
  }

  return (
    <div className="flex min-h-dvh w-full">
      <NumerolojiAdminSidebar
        mode="links"
        isDark={isDark}
        onToggleTheme={() => setTheme(isDark ? "light" : "dark")}
      />

      <div className="flex min-w-0 flex-1 flex-col bg-slate-100/60 dark:bg-slate-900/30">
        <header className="flex h-16 items-center justify-between border-b border-slate-200/70 bg-white/70 px-4 backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/40 sm:px-6">
          <div className="text-base font-semibold tracking-tight text-slate-900 dark:text-slate-50 sm:text-lg">Manuel İstek</div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              className="grid h-10 w-10 place-items-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-300"
              aria-label="Bildirimler"
            >
              <Bell className="h-4 w-4" />
            </button>

            <div className="hidden items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2 dark:border-slate-800 dark:bg-slate-950/40 sm:flex">
              <div className="grid h-8 w-8 place-items-center rounded-full bg-orange-100 text-sm font-semibold text-orange-700 dark:bg-orange-500/15 dark:text-orange-300">
                A
              </div>
              <div className="leading-tight">
                <div className="text-sm font-semibold">Admin Panel</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Süper Yetkili</div>
              </div>
            </div>

            <Button
              variant="outline"
              className="rounded-full"
              size="sm"
              onClick={() => logoutMutation.mutate()}
              disabled={logoutMutation.isPending}
            >
              Çıkış
            </Button>
          </div>
        </header>

        {sessionQuery.data?.numerolojiStorage === "memory" ? (
          <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100 sm:px-6">
            Uyarı: Veri tabanı bağlantısı sunucuda aktif değil. Oluşturulan manuel istekler kalıcı kaydedilmez ve
            sunucu yenilenince kaybolabilir. (Sunucuda SUPABASE_URL + SUPABASE_SERVICE_ROLE_KEY gerekli.)
          </div>
        ) : null}

        <div className="min-w-0 flex-1 p-4 sm:p-6">
          <div className="mx-auto w-full max-w-3xl">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <div className="text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-50">Manuel istek oluştur</div>
                <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Bu istek admin tarafından oluşturulur. Gerekirse daha sonra “Gönder” ile işaretlenir.
                </div>
              </div>

              <Button asChild variant="outline" className="rounded-full">
                <Link to="/admin/numeroloji">Liste</Link>
              </Button>
            </div>

            <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950/40">
              <form
                className="grid gap-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (mutation.isPending) return;

                  mutation.mutate({
                    packageKey: selectedPackage,
                    reportLang: parseLang(reportLang),
                    ad,
                    soyad,
                    email,
                    gun: showBirthDate ? Number(gun) : undefined,
                    ay: showBirthDate ? Number(ay) : undefined,
                    yil: showBirthDate ? Number(yil) : undefined,
                    partnerAd: isRelationship ? partnerAd : undefined,
                    partnerSoyad: isRelationship ? partnerSoyad : undefined,
                    partnerGun: isRelationship ? Number(partnerGun) : undefined,
                    partnerAy: isRelationship ? Number(partnerAy) : undefined,
                    partnerYil: isRelationship ? Number(partnerYil) : undefined,
                  });
                }}
              >
                <div className="grid gap-2">
                  <Label>Paket</Label>
                  <select
                    className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none ring-blue-600/20 focus:ring-4 dark:border-slate-800 dark:bg-slate-950/30 dark:text-slate-50"
                    value={selectedPackage}
                    onChange={(e) => setSelectedPackage(normalizePackage(e.target.value))}
                  >
                    <option value="normal">Numeroloji Analizi</option>
                    <option value="premium">Premium (Full)</option>
                    <option value="pin">Pin Kodu Analizi</option>
                    <option value="isim">İsim Analizi</option>
                    <option value="iliski">İlişki Analizi</option>
                  </select>
                </div>

                <div className="grid gap-2">
                  <Label>Rapor dili</Label>
                  <select
                    className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none ring-blue-600/20 focus:ring-4 dark:border-slate-800 dark:bg-slate-950/30 dark:text-slate-50"
                    value={reportLang}
                    onChange={(e) => setReportLang(e.target.value)}
                  >
                    <option value="tr">Türkçe</option>
                    <option value="en">English</option>
                    <option value="es">Español</option>
                    <option value="de">Deutsch</option>
                  </select>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="grid gap-2">
                    <Label htmlFor="ad">Ad</Label>
                    <Input id="ad" value={ad} onChange={(e) => setAd(e.target.value)} required />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="soyad">Soyad</Label>
                    <Input id="soyad" value={soyad} onChange={(e) => setSoyad(e.target.value)} required />
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="email">E-posta</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                </div>

                {showBirthDate ? (
                  <div className="grid gap-3 sm:grid-cols-3">
                    <div className="grid gap-2">
                      <Label htmlFor="gun">Gün</Label>
                      <Input id="gun" inputMode="numeric" value={gun} onChange={(e) => setGun(e.target.value)} required />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="ay">Ay</Label>
                      <Input id="ay" inputMode="numeric" value={ay} onChange={(e) => setAy(e.target.value)} required />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="yil">Yıl</Label>
                      <Input id="yil" inputMode="numeric" value={yil} onChange={(e) => setYil(e.target.value)} required />
                    </div>
                  </div>
                ) : null}

                {isRelationship ? (
                  <div className="mt-2 grid gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/20">
                    <div className="text-sm font-semibold">Kişi B</div>

                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="grid gap-2">
                        <Label htmlFor="partnerAd">Ad</Label>
                        <Input id="partnerAd" value={partnerAd} onChange={(e) => setPartnerAd(e.target.value)} required />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="partnerSoyad">Soyad</Label>
                        <Input id="partnerSoyad" value={partnerSoyad} onChange={(e) => setPartnerSoyad(e.target.value)} required />
                      </div>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-3">
                      <div className="grid gap-2">
                        <Label htmlFor="partnerGun">Gün</Label>
                        <Input id="partnerGun" inputMode="numeric" value={partnerGun} onChange={(e) => setPartnerGun(e.target.value)} required />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="partnerAy">Ay</Label>
                        <Input id="partnerAy" inputMode="numeric" value={partnerAy} onChange={(e) => setPartnerAy(e.target.value)} required />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="partnerYil">Yıl</Label>
                        <Input id="partnerYil" inputMode="numeric" value={partnerYil} onChange={(e) => setPartnerYil(e.target.value)} required />
                      </div>
                    </div>
                  </div>
                ) : null}

                {mutation.isError ? (
                  <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                    {(mutation.error as Error).message}
                  </div>
                ) : null}

                <Button type="submit" className="mt-1 w-full rounded-full" disabled={mutation.isPending}>
                  {mutation.isPending ? "Oluşturuluyor..." : "İstek oluştur"}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
